import { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Center,
  Stack,
  Spinner
} from '@chakra-ui/react';
import { ListingDataSuggestionModel, ArtistModel, PaginationModel } from 'models';
import { ProcessStatus } from 'models/types';
import { SuggestionType } from 'models/ListingDataSuggestion/types';
import { ListingDataSuggestionCard } from 'components';
import ListingDataSuggestionsFiltersForm from '../../Form/ListingDataSuggestionsFiltersForm/ListingDataSuggestionsFiltersForm';
import EmptyState from '../../EmptyState/EmptyState';
import PaginationFooter from '../../Pagination/PaginationFooter/PaginationFooter';
import { filterListingsByQuery } from 'utils/search';

const PER_PAGE = 10;

export interface DetailedListingDataSuggestionGridProps {
  suggestions: ListingDataSuggestionModel[];
  isLoading: boolean;
  onApprove?: (suggestionId: string) => void;
  onReject?: (suggestionId: string) => void;
  pagination?: PaginationModel<ListingDataSuggestionModel>;
  onPageChange?: (page: number) => void;
  defaultStatus?: ProcessStatus;
  defaultType?: SuggestionType;
  onStatusChange?: (status: ProcessStatus) => void;
  isActionLoading?: boolean;
  availableArtists?: ArtistModel[];
  selectionMode?: boolean;
  selectedSuggestions?: ListingDataSuggestionModel[];
  onSuggestionSelect?: (suggestion: ListingDataSuggestionModel) => void;
  onFilteredSuggestionsChange?: (suggestions: ListingDataSuggestionModel[]) => void;
}

export default function DetailedListingDataSuggestionGrid({
  suggestions,
  isLoading,
  onApprove,
  onReject,
  pagination,
  onPageChange,
  defaultStatus = ProcessStatus.PENDING,
  defaultType = SuggestionType.ALL,
  onStatusChange,
  isActionLoading = false,
  availableArtists = [],
  selectionMode = false,
  selectedSuggestions = [],
  onSuggestionSelect,
  onFilteredSuggestionsChange
}: DetailedListingDataSuggestionGridProps) {
  const [selectedStatus, setSelectedStatus] = useState<ProcessStatus>(defaultStatus);
  const [selectedType, setSelectedType] = useState<SuggestionType>(defaultType);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedArtists, setSelectedArtists] = useState<ArtistModel[]>([]);
  const [page, setPage] = useState(1);

  const isOnFilterMode = (): boolean => {
    return searchQuery.length > 0 || selectedType !== SuggestionType.ALL || selectedArtists.length > 0;
  };

  const filteredSuggestions = useMemo(() => {
    let filtered = suggestions;

    // Filter by artists
    if (selectedArtists.length > 0) {
      const artistIds = new Set(selectedArtists.map(artist => artist.id));
      filtered = filtered.filter(suggestion => artistIds.has(suggestion.listing.artistId));
    }

    // Filter by type
    if (selectedType !== SuggestionType.ALL) {
      filtered = filtered.filter(suggestion => {
        switch (selectedType) {
          case SuggestionType.ORIGINAL:
            return suggestion.isOriginalSuggestion();
          case SuggestionType.SET:
            return suggestion.isSetSuggestion();
          case SuggestionType.FAKE:
            return suggestion.isFakeSuggestion();
          default:
            return true;
        }
      });
    }

    // Filter by search query
    if (searchQuery?.trim()) {
      // 1) Collect the listings from filtered suggestions
      const listings = filtered.map(s => s.listing);

      // 2) Reuse your util to filter listings
      const filteredListings = filterListingsByQuery(listings, searchQuery);

      // 3) Build a quick lookup and filter suggestions
      const allowedIds = new Set(filteredListings.map(listing => listing.id));
      filtered = filtered.filter(s => allowedIds.has(s.listing.id));
    }

    return filtered;
  }, [suggestions, searchQuery, selectedType, selectedArtists]);

  // Memoize paginated suggestions
  const paginatedSuggestions = useMemo(() => {
    if (!isOnFilterMode() && pagination) {
      return filteredSuggestions;
    }
    const startIdx = (page - 1) * PER_PAGE;
    const endIdx = startIdx + PER_PAGE;
    return filteredSuggestions.slice(startIdx, endIdx);
  }, [filteredSuggestions, page, pagination, searchQuery, selectedType, selectedArtists]);

  // Notify parent of filtered suggestions changes (for Select All)
  useEffect(() => {
    onFilteredSuggestionsChange?.(paginatedSuggestions);
  }, [paginatedSuggestions, onFilteredSuggestionsChange]);

  // Calculate total pages based on filter mode vs server mode
  const filteredTotalPages = Math.ceil(filteredSuggestions.length / PER_PAGE);
  const totalPages = !isOnFilterMode() && pagination?.totalPages
    ? pagination.totalPages
    : filteredTotalPages;
  const currentPage = !isOnFilterMode() && pagination?.currentPage
    ? pagination.currentPage
    : page;

  const handlePageChange = (newPage: number) => {
    if (!isOnFilterMode() && onPageChange) {
      onPageChange(newPage);
      return;
    }
    setPage(newPage);
  };

  const handleStatusChange = (status: ProcessStatus) => {
    setSelectedStatus(status);
    setPage(1); // Reset to first page when status changes
    onStatusChange?.(status);
  };

  const handleTypeChange = (type: SuggestionType) => {
    setSelectedType(type);
    setPage(1); // Reset to first page when type changes
  };

  const handleQueryChange = (query: string) => {
    setSearchQuery(query);
    setPage(1); // Reset to first page when search changes
  };

  const handleArtistChange = (artists: ArtistModel[]) => {
    setSelectedArtists(artists);
    setPage(1); // Reset to first page when artists change
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedType(SuggestionType.ALL);
    setSelectedArtists([]);
    setPage(1);
  };

  const renderSuggestionCards = () => {
    if (isLoading) {
      return (
        <Center p={8}>
          <Spinner size="xl" />
        </Center>
      );
    }

    if (!filteredSuggestions?.length) {
      return (
        <EmptyState
          header="No Data Suggestions"
          description="No Results Found"
          showButton={isOnFilterMode()}
          onButtonClick={handleClearFilters}
          buttonText="Clear Filters"
        />
      );
    }

    return (
      <Stack spacing={4}>
        {paginatedSuggestions.map(suggestion => (
          <ListingDataSuggestionCard
            key={`suggestion_card_${suggestion.id}`}
            suggestion={suggestion}
            onApprove={onApprove}
            onReject={onReject}
            isLoading={isActionLoading}
            selectionMode={selectionMode}
            selected={selectedSuggestions.some(s => s.id === suggestion.id)}
            onSelect={() => onSuggestionSelect?.(suggestion)}
          />
        ))}
      </Stack>
    );
  };

  return (
    <Box width="100%">
      <Box mb={6}>
        <ListingDataSuggestionsFiltersForm
          defaultStatus={selectedStatus}
          defaultType={selectedType}
          defaultQuery={searchQuery}
          defaultArtists={selectedArtists}
          availableArtists={availableArtists}
          onStatusChange={handleStatusChange}
          onQueryChange={handleQueryChange}
          onTypeChange={handleTypeChange}
          onArtistChange={handleArtistChange}
        />
      </Box>
      {renderSuggestionCards()}
      {totalPages > 1 && (
        <Box mt={8}>
          <PaginationFooter
            currentPage={currentPage}
            lastPage={totalPages}
            siblingsCount={1}
            onPageChange={handlePageChange}
          />
        </Box>
      )}
    </Box>
  );
}
